<script setup lang="ts">
import { ImagePlus as ImagePlusIcon, Route as RouteIcon, Upload as UploadIcon } from '@lucide/vue'

const props = withDefaults(
  defineProps<{
    stacked?: boolean
    busy?: boolean
  }>(),
  {
    stacked: false,
    busy: false,
  },
)

const emit = defineEmits<{
  'import-gpx': []
  'import-photo': []
  'draw-route': []
}>()
</script>

<template>
  <div
    :class="[
      'grid gap-3',
      props.stacked ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-2',
      props.busy ? 'opacity-70' : '',
    ]"
  >
    <div
      class="relative overflow-hidden rounded-2xl border border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-5 sm:p-6"
    >
      <div class="flex h-full flex-col justify-between gap-5">
        <div class="space-y-3">
          <div
            class="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600/10 text-emerald-700"
          >
            <UploadIcon class="h-5 w-5" />
          </div>
          <div class="space-y-1.5">
            <h3 class="text-base font-semibold text-gray-900">Import or drop files</h3>
            <p class="text-sm leading-6 text-gray-600">
              Drag GPX, FIT, or photo files onto the map, or use the actions below to add them to
              this collection.
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            :disabled="props.busy"
            class="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:cursor-pointer hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            @click="emit('import-gpx')"
          >
            <UploadIcon class="h-4 w-4" />
            <span>Import GPX / FIT</span>
          </button>
          <button
            type="button"
            :disabled="props.busy"
            class="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:cursor-pointer hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            @click="emit('import-photo')"
          >
            <ImagePlusIcon class="h-4 w-4 text-sky-600" />
            <span>Add photos</span>
          </button>
        </div>
      </div>
    </div>

    <div
      class="relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-5 sm:p-6"
    >
      <div class="flex h-full flex-col justify-between gap-5">
        <div class="space-y-3">
          <div
            class="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500/12 text-amber-700"
          >
            <RouteIcon class="h-5 w-5" />
          </div>
          <div class="space-y-1.5">
            <h3 class="text-base font-semibold text-gray-900">Draw route</h3>
            <p class="text-sm leading-6 text-gray-600">
              Start a route from scratch and place points directly on the map. Nothing is saved
              until you confirm the route in track routing mode.
            </p>
          </div>
        </div>

        <div>
          <button
            type="button"
            :disabled="props.busy"
            class="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:cursor-pointer hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            @click="emit('draw-route')"
          >
            <RouteIcon class="h-4 w-4 text-amber-700" />
            <span>Start drawing</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
